import * as THREE from 'three';

// Props mark themselves with `userData.collideR` / `collideH` so the placement
// stage can register a collider. Missing fields mean "brushable" (flowers).

export function makeTree() {
  // Outer group takes placement (world position/rotation), inner sway group
  // carries the geometry so wind can tilt the tree at its base without
  // fighting the surface-normal alignment.
  const outer = new THREE.Group();
  const sway = new THREE.Group();
  outer.add(sway);

  const trunkMat = new THREE.MeshStandardMaterial({ color: 0x4a2f18, roughness: 1 });
  const leafMat  = new THREE.MeshStandardMaterial({
    color: 0x2e6a22,
    roughness: 0.9,
    flatShading: true,
  });

  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.13, 0.9, 7), trunkMat);
  trunk.position.y = 0.45;
  trunk.castShadow = true;
  trunk.receiveShadow = true;
  sway.add(trunk);

  const c1 = new THREE.Mesh(new THREE.ConeGeometry(0.55, 1.3, 8), leafMat);
  c1.position.y = 1.3;
  c1.castShadow = true;
  sway.add(c1);

  const c2 = new THREE.Mesh(new THREE.ConeGeometry(0.4, 0.95, 8), leafMat);
  c2.position.y = 1.75;
  c2.castShadow = true;
  sway.add(c2);

  outer.userData.collideR = 0.30;
  outer.userData.collideH = 1.9;
  outer.userData.swayGroup = sway;
  outer.userData.swayAmount = 1;
  return outer;
}

export function makeRock() {
  const r = 0.25 + Math.random() * 0.22;
  const mesh = new THREE.Mesh(
    new THREE.DodecahedronGeometry(r, 0),
    new THREE.MeshStandardMaterial({ color: 0x808080, roughness: 1, flatShading: true }),
  );
  mesh.position.y = r * 0.55;
  mesh.rotation.set(Math.random() * 2, Math.random() * 2, Math.random() * 2);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.collideR = r * 0.9;
  mesh.userData.collideH = r * 1.4;
  return mesh;
}

// Small sway-able grass cluster — three narrow cones arranged in a tight
// triangle. No collider (you walk through it), floppier sway than flowers.
export function makeGrass() {
  const outer = new THREE.Group();
  const sway = new THREE.Group();
  outer.add(sway);

  const mat = new THREE.MeshStandardMaterial({
    color: 0x5a9a38,
    roughness: 1,
    flatShading: true,
  });
  const bladeGeo = new THREE.ConeGeometry(0.035, 0.22, 3);
  bladeGeo.translate(0, 0.11, 0);

  for (let i = 0; i < 3; i++) {
    const blade = new THREE.Mesh(bladeGeo, mat);
    const a = i * (Math.PI * 2 / 3) + Math.random() * 0.4;
    const r = 0.015 + Math.random() * 0.025;
    blade.position.set(Math.cos(a) * r, 0, Math.sin(a) * r);
    blade.rotation.x = (Math.random() - 0.5) * 0.2;
    blade.rotation.z = (Math.random() - 0.5) * 0.25;
    sway.add(blade);
  }

  outer.userData.swayGroup = sway;
  outer.userData.swayAmount = 2.2;    // floppier than trees / flowers
  return outer;
}

// Bioluminescent ground pods — cluster of 2-4 small mushroom-like growths,
// all sharing one emissive material so they pulse in unison. Different
// clusters carry different colours and phases. Subtle in daylight, lovely at
// night against the dark grass.
const GLOW_POD_COLORS = [0x50d8a0, 0x60d0ff, 0xa080ff, 0xff80a0, 0xffd060];
const GLOW_POD_STEM = new THREE.MeshStandardMaterial({ color: 0x7a7066, roughness: 1 });

export function makeGlowPod() {
  const outer = new THREE.Group();
  const sway = new THREE.Group();
  outer.add(sway);

  const color = GLOW_POD_COLORS[Math.floor(Math.random() * GLOW_POD_COLORS.length)];
  const mat = new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.5,
    roughness: 0.55,
    metalness: 0,
  });

  const count = 2 + Math.floor(Math.random() * 3);   // 2..4 per cluster
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist  = Math.random() * 0.08;
    const cx = Math.cos(angle) * dist;
    const cz = Math.sin(angle) * dist;
    const s  = 0.7 + Math.random() * 0.6;
    const stemH = 0.05 + Math.random() * 0.035;

    const stem = new THREE.Mesh(
      new THREE.CylinderGeometry(0.015 * s, 0.02 * s, stemH, 5),
      GLOW_POD_STEM,
    );
    stem.position.set(cx, stemH / 2, cz);
    stem.castShadow = true;
    sway.add(stem);

    const cap = new THREE.Mesh(
      new THREE.SphereGeometry(0.042 * s, 14, 9, 0, Math.PI * 2, 0, Math.PI * 0.55),
      mat,
    );
    cap.position.set(cx, stemH, cz);
    cap.castShadow = true;
    sway.add(cap);
  }

  outer.userData.swayGroup = sway;
  outer.userData.swayAmount = 0.9;         // tiny flutter in the breeze
  outer.userData.glowPulse = {
    material: mat,
    phase: Math.random() * Math.PI * 2,
    rate:  0.5 + Math.random() * 0.5,       // per-cluster breathing rate
    base:  0.4,
    amp:   0.45,
  };
  return outer;
}

export function makeFlower() {
  const outer = new THREE.Group();
  const sway = new THREE.Group();
  outer.add(sway);

  const stem = new THREE.Mesh(
    new THREE.CylinderGeometry(0.015, 0.015, 0.3, 5),
    new THREE.MeshStandardMaterial({ color: 0x4a8a30, roughness: 1 }),
  );
  stem.position.y = 0.15;
  sway.add(stem);

  const petal = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 8, 6),
    new THREE.MeshStandardMaterial({
      color: Math.random() < 0.5 ? 0xffcf50 : 0xff7fb5,
      roughness: 0.8,
    }),
  );
  petal.position.y = 0.32;
  petal.castShadow = true;
  sway.add(petal);

  // No collider — flowers are brushable.
  outer.userData.swayGroup = sway;
  outer.userData.swayAmount = 1.6;   // floppier than trees
  return outer;
}
